/**
 * membershipTemplates.service.ts — Admin > Memberships and Admin > Packages.
 *
 * Both are membership templates (memberships_template), read and written
 * through /v5/memberships as the corporate dashboard does:
 *   - memberships are incrementType 0 / 1 (no increment_type filter; a new one
 *     is created as 1), packages are incrementType 2;
 *   - a package also has availability settings (packages_addons) at
 *     /v5/packages/{id}/addon;
 *   - deleted = endDate set; restore clears it.
 * An update replaces each list it is sent (products, procedures, tags,
 * facilities, plan types, categories), so lists are always sent, empty ones
 * as [] — corp drops empty lists, which is why clearing one never saved
 * there. The image is not sent on update: the picker assigns it at once, and
 * an empty value would delete it. Writes need Admin Access.
 *
 * (memberships.service.ts keeps the patient-side membership calls.)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type TemplateKind = 'membership' | 'package';

export interface PackageAvailability {
  /** 0 = Standard, 1 = Promo. */
  packageType: number;
  type: 'unlimited' | 'date-range';
  /** 0 = Everyone, 1 = Non-members, 2 = Members. */
  availableTo: number;
  /** "YYYY-MM-DD" in the account's calendar, or ''. */
  startDate: string;
  stopDate: string;
  /** 0 = unlimited. */
  maxUses: number;
  maxPatient: number;
}

export interface TemplateListItem {
  membershipTemplateID: number;
  title: string;
  description: string;
  active: boolean;
  membershipTotal: number;
  membershipImage: string;
  publicStatus: boolean;
  productItems: { name: string; quantity: number }[];
  procedureItems: { name: string; quantity: number }[];
  productsCount: number;
  proceduresCount: number;
  /** Packages only. */
  availability: PackageAvailability | null;
  categories: { id: number; name: string }[];
}

export interface TemplateProduct {
  productID: number;
  name: string;
  quantity: number;
  curRate: number;
  taxable: boolean;
  expireDays: number;
  expireMonths: number;
}

export interface TemplateProcedure {
  procSubID: number;
  /** Procedure category (procedures_categories). */
  categoryID: number;
  /** Sub category (procedures_subcategories), legacy's `procID`. */
  procID: number;
  name: string;
  /** Saved names of the category and sub category, for ones since retired. */
  categoryName: string;
  subcategoryName: string;
  /** Normal price. */
  price: number;
  effectivePrice: number;
  /** Hours (expNumHours): an hour band of the account's OR-fee scale. */
  hours: number;
  /** Description (laymansTerms). */
  description: string;
  expireDays: number;
  expireMonths: number;
}

export interface TemplateDetail {
  membershipTemplateID: number;
  title: string;
  description: string;
  membershipTotal: number;
  membershipImage: string;
  publicStatus: boolean;
  requiredDuration: number;
  cancelDuration: number;
  /** The agreement's groupID; 0 = none. */
  membershipAgreementID: number;
  products: TemplateProduct[];
  procedures: TemplateProcedure[];
  tagIds: number[];
  facilityIds: number[];
  planTypeIds: number[];
  categoryIds: number[];
}

export interface TemplatePayload {
  title: string;
  description: string;
  membershipTotal: number;
  publicStatus: boolean;
  requiredDuration: number;
  cancelDuration: number;
  membershipAgreementID: number;
  products: { productID: number; quantity: number; curRate: number; taxable: boolean; expireDays: number; expireMonths: number }[];
  procedures: {
    procSubID: number;
    /** Left out when unknown: the API takes them from the procedure. */
    categoryID?: number;
    procID?: number;
    quantity: number;
    price: number;
    effectivePrice: number;
    expNumHours: number;
    laymansTerms: string;
    expireDays: number;
    expireMonths: number;
  }[];
  tags: number[];
  facilities: number[];
  plan_types: number[];
  product_categories?: number[];
}

type Raw = Record<string, unknown>;

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (v == null ? '' : String(v));
const flag = (v: unknown): boolean => v === true || v === 1 || v === '1';
const ids = (v: unknown): number[] =>
  (Array.isArray(v) ? v : []).map((x) => num(x && typeof x === 'object' ? (x as Raw).id : x)).filter((n) => n > 0);

/** '' for the zero date; otherwise the calendar date. */
function day(v: unknown): string {
  const s = str(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !s.startsWith('0000') ? s : '';
}

export function normalizeAvailability(raw: unknown): PackageAvailability | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Raw;
  return {
    packageType: num(a.packageType) === 1 ? 1 : 0,
    type: str(a.type) === 'date-range' ? 'date-range' : 'unlimited',
    availableTo: [0, 1, 2].includes(num(a.availableTo)) ? num(a.availableTo) : 0,
    startDate: day(a.startDate),
    stopDate: day(a.stopDate),
    maxUses: num(a.maxUses),
    maxPatient: num(a.maxPatient),
  };
}

function normalizeListItem(t: Raw): TemplateListItem {
  const items = (v: unknown) =>
    (Array.isArray(v) ? v : []).map((i: Raw) => ({ name: str(i.name), quantity: num(i.quantity) || 1 }));
  return {
    membershipTemplateID: num(t.membershipTemplateID),
    title: str(t.title),
    description: str(t.description),
    active: t.active === undefined ? !flag(t.deleted) : flag(t.active),
    membershipTotal: num(t.membershipTotal),
    membershipImage: str(t.membershipImage).trim(),
    publicStatus: num(t.publicStatus) === 1 || t.publicStatus === true,
    productItems: items(t.productItems),
    procedureItems: items(t.procedureItems),
    productsCount: num(t.products_count),
    proceduresCount: num(t.procedures_count),
    availability: normalizeAvailability(t.packageAddon),
    categories: (Array.isArray(t.productCategories) ? t.productCategories : []).map((c: Raw) => ({ id: num(c.id), name: str(c.name) })),
  };
}

function kindParams(kind: TemplateKind): Record<string, number> {
  return kind === 'package' ? { increment_type: 2 } : {};
}

/** Every template of the kind, deleted ones too (every page of the grouped list, flattened). */
export async function listTemplates(kind: TemplateKind): Promise<TemplateListItem[]> {
  const out: TemplateListItem[] = [];
  for (let page = 1; page <= 50; page++) {
    const r = await apiClient.get<{ data: Raw[]; meta?: { last_page?: number } }>('/memberships', {
      params: { ...kindParams(kind), all_statuses: 'true', limit: 500, page },
    });
    const groups = Array.isArray(r.data?.data) ? r.data.data : [];
    for (const g of groups) for (const t of Array.isArray(g.templates) ? (g.templates as Raw[]) : []) out.push(normalizeListItem(t));
    if (page >= num(r.data?.meta?.last_page || 1)) break;
  }
  return out;
}

export async function getTemplate(kind: TemplateKind, id: number): Promise<TemplateDetail> {
  const r = await apiClient.get<ApiResponse<Raw>>(`/memberships/${id}`, { params: kindParams(kind) });
  const d = (r.data?.data ?? {}) as Raw;
  return {
    membershipTemplateID: num(d.membershipTemplateID) || id,
    title: str(d.title),
    description: str(d.description),
    membershipTotal: num(d.membershipTotal),
    membershipImage: str(d.membershipImage).trim(),
    publicStatus: num(d.publicStatus) === 1 || d.publicStatus === true,
    requiredDuration: num(d.requiredDuration),
    cancelDuration: d.cancelDuration == null ? 30 : num(d.cancelDuration),
    membershipAgreementID: num(d.membershipAgreementID),
    products: (Array.isArray(d.products) ? (d.products as Raw[]) : []).map((p) => ({
      productID: num(p.productID ?? p.product_id),
      name: str(p.name),
      quantity: num(p.quantity) || 1,
      curRate: num(p.curRate ?? p.fee),
      taxable: flag(p.taxable),
      expireDays: num(p.expireDays),
      expireMonths: num(p.expireMonths),
    })),
    procedures: (Array.isArray(d.procedures) ? (d.procedures as Raw[]) : []).map((p) => ({
      procSubID: num(p.procSubID),
      categoryID: num(p.categoryID),
      procID: num(p.procID),
      name: str(p.name),
      categoryName: str(p.categoryName),
      subcategoryName: str(p.subcategoryName),
      // The API's `price` is the effective price; the normal price is `normalPrice` (`fee` before it).
      price: num(p.normalPrice ?? p.fee ?? p.price),
      effectivePrice: num(p.effectivePrice ?? p.price),
      hours: num(p.expNumHours),
      description: str(p.laymansTerms),
      expireDays: num(p.expireDays),
      expireMonths: num(p.expireMonths),
    })),
    tagIds: ids(d.tags),
    facilityIds: ids(d.facilities),
    planTypeIds: ids(d.plan_types),
    categoryIds: ids(d.product_categories),
  };
}

/** Creates the template; returns its id. A membership is created as monthly (1), as corp does. */
export async function createTemplate(kind: TemplateKind, data: TemplatePayload & { membershipImage?: string }): Promise<number> {
  const body: Raw = { ...data, mtOrder: 1 };
  if (kind === 'package') body.incrementType = 2;
  const r = await apiClient.post<ApiResponse<Raw>>('/memberships', body);
  return num(r.data?.data?.membershipTemplateID ?? r.data?.data?.id);
}

export function updateTemplate(id: number, data: TemplatePayload): Promise<void> {
  return apiClient.patch(`/memberships/${id}`, data).then(() => undefined);
}

export function deleteTemplate(kind: TemplateKind, id: number): Promise<void> {
  return apiClient.delete(`/memberships/${id}`, { params: kindParams(kind) }).then(() => undefined);
}

export function restoreTemplate(kind: TemplateKind, id: number): Promise<void> {
  return apiClient.post(`/memberships/${id}/restore`, null, { params: kindParams(kind) }).then(() => undefined);
}

export async function getPackageAvailability(id: number): Promise<PackageAvailability | null> {
  const r = await apiClient.get<ApiResponse<Raw | null>>(`/packages/${id}/addon`);
  return normalizeAvailability(r.data?.data);
}

export function savePackageAvailability(id: number, a: PackageAvailability): Promise<void> {
  const body: Raw = {
    packageType: a.packageType,
    type: a.type,
    availableTo: a.availableTo,
    maxUses: a.maxUses,
    maxPatient: a.maxPatient,
  };
  if (a.type === 'date-range') {
    body.startDate = a.startDate;
    body.stopDate = a.stopDate;
  }
  return apiClient.post(`/packages/${id}/addon`, body).then(() => undefined);
}

// ─── Options ───────────────────────────────────────────────────────────────────

/** A procedure as the template row picks it, with what picking it fills in. */
export interface ProcedureOption {
  procSubID: number;
  name: string;
  /** The catalog price: both Normal and Effective price start from it. */
  feeToPatient: number;
  /** Expected hours; the row snaps it to an hour band. */
  expNumHours: number;
  /** Fills the row's Description. */
  laymansTerms: string;
  /** The procedure's own anesthesia and OR fees, which a quote line carries (quote presets). */
  anesthesiaFee?: number;
  operatingRoomFee?: number;
}

export interface ProcedureSubcategoryOption {
  procID: number;
  name: string;
  procedures: ProcedureOption[];
}

export interface ProcedureCategoryOption {
  categoryID: number;
  name: string;
  subcategories: ProcedureSubcategoryOption[];
}

/**
 * The account's active Category → Sub Category → Procedure tree, in the
 * catalog's order: legacy's three cascading selects on a template's procedure.
 */
export function listProcedureTree(): Promise<ProcedureCategoryOption[]> {
  return apiClient.get<ApiResponse<Raw[]>>('/procedure-categories/with-procedures').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : []).map((c) => ({
      categoryID: num(c.procedureCategoryID),
      name: str(c.name),
      subcategories: (Array.isArray(c.subcategories) ? (c.subcategories as Raw[]) : []).map((s) => ({
        procID: num(s.procedureSubCategoryID),
        name: str(s.name),
        procedures: (Array.isArray(s.procedures) ? (s.procedures as Raw[]) : []).map((p) => ({
          procSubID: num(p.procedureSpecificID),
          name: str(p.name),
          feeToPatient: num(p.feeToPatient),
          expNumHours: num(p.expNumHours),
          laymansTerms: str(p.laymansTerms),
          anesthesiaFee: num(p.anesthesiaFee),
          operatingRoomFee: num(p.operatingRoomFee),
        })),
      })),
    })),
  );
}

/** The Hours choices: the OR-fee times of the account's facility with the most hours. */
export function listProcedureHourBands(): Promise<number[]> {
  return apiClient
    .get<ApiResponse<unknown[]>>('/memberships/procedure-hour-bands')
    .then((r) => (Array.isArray(r.data?.data) ? r.data.data.map(num).filter((n) => n > 0) : []));
}

export interface ProductTypeCategoryLink {
  productTypeID: number;
  categoryID: number;
  name: string;
}

/** Product type ↔ product-type-category links, with names (packages' inherited categories). */
export function listProductTypeCategoryLinks(): Promise<ProductTypeCategoryLink[]> {
  return apiClient.get<ApiResponse<Raw[]>>('/product-type-categories').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : [])
      .map((row) => {
        const cat = (row.product_category ?? row.productCategory ?? null) as Raw | null;
        return {
          productTypeID: num(row.productTypeID ?? row.product_type_id),
          categoryID: num(row.productCategoryID ?? cat?.productCategoryID),
          name: str(cat?.productCategoryName ?? cat?.title).trim(),
        };
      })
      .filter((l) => l.productTypeID > 0 && l.categoryID > 0),
  );
}
